import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@/shared/api/httpClient";
import { posterService, type PosterAssetDto, type PosterTaskDto } from "../api/posterService";

/**
 * D9(2026-07-30)— 这次拉取是**再拉也没用**的错误吗?
 *
 * 后端对「不存在」和「越权」统一返 404 `POSTER_TASK_NOT_FOUND`(PosterAssetEditService:47
 * 「不泄漏存在性」),403 是路由级角色不足。两者都不会因为再等 5 秒就变了 ——
 * 而 `tick()` 原来的 `catch {}` 把它们和网络抖动混成一件事,继续每 5s 重试到超时。
 *
 * 实测现象(W1 走查):`tcanary_mgr`(实为 TENANT_STAFF)打开别人建的任务详情,
 * 页面**永远停在「加载中…」**,五分钟后才冒一句「轮询超时」——用户读到的是「系统很慢」,
 * 真相是「你没权限看这条」。可见的失败优于隐形的等待。
 *
 * 401 不在此列:httpClient 自己会清 token 跳登录,轮询层不该插手。
 */
export function isTerminalFetchError(e: unknown): boolean {
  return e instanceof ApiError && (e.httpStatus === 404 || e.httpStatus === 403);
}

/**
 * V34 (2026-05-26) — 合并新拉到的 assets,对 id 未变的 asset 复用旧的 signed URL
 * (thumbUrl/baseUrl)。
 *
 * <p>thumbUrl 是每次 listAssets 重新签名的 presigned URL,query string 每次都变。直接
 * setAssets(整批新对象)会让所有 `<img src>` 变化 → 浏览器重载全部图片。表现为「改图并保留
 * 原图」完成时,没动过的原图也跟着闪一下重载(用户报"两张图片一起刷新")。
 *
 * <p>改图/重画(保留或替换)都会产生**新 asset id**(替换是软删旧行 + 插新行),所以同 id
 * 一定是内容没变的图,复用旧 URL 不会漏掉真正的更新;overlay/filter/selectedAt 等元数据仍取
 * 新值(展开 ...next 在前)。新 id 用新对象正常加载。
 */
export function mergeAssetUrls(prev: PosterAssetDto[], next: PosterAssetDto[]): PosterAssetDto[] {
  if (prev.length === 0) return next;
  const prevById = new Map(prev.map((p) => [p.id, p]));
  return next.map((n) => {
    const old = prevById.get(n.id);
    if (!old) return n;
    return { ...n, thumbUrl: old.thumbUrl ?? n.thumbUrl, baseUrl: old.baseUrl ?? n.baseUrl };
  });
}

/**
 * 这一拍要不要拉产物 —— W1(2026-08-02)。
 *
 * ═══ 改前是什么样 ═════════════════════════════════════════════════════════════
 * `listAssets` 被一条 terminal-only 的判据锁着:只有任务到 SUCCESS/PARTIAL/FAILED/
 * CANCELLED 才拉,PENDING/RUNNING 期间**一次都不拉**。真跑坐实:后端已经交付 2 张、
 * 页头如实写着「2/6 成功」,页面照样六个骨架全在转、`assets=[]`,组图续出条还说
 * 「封面还没出好」—— 而封面早就出好了。
 *
 * 这条判据当初的假设是「产物只有终态才有意义」。ai 侧 `_generate_one` 是**单张**
 * gen+compose+upload、每张各自回调 `/tasks/{id}/assets`,backend `recordAsset` 写完即
 * `recomputeStatus`(「海报每 seq 都写 asset(含 FAILED),recomputeStatus 自闭环」)——
 * 组图更是封面先单独出完再并行出其余。也就是说**产物一直是逐张落库的**,
 * 假设从组图分步上线那天起就不成立了。
 *
 * ═══ 为什么不是「每拍都拉」═══════════════════════════════════════════════════
 * 5s 一拍,一单 6 张要跑一两分钟 → 每拍都拉就是 20+ 次 `listAssets`,而其中真正有新东西的
 * 只有 6 次。`successCount + failedCount` 由 backend 随产物 recompute,是**免费的变化信号**
 * (它跟着 task DTO 一起回来,不额外发请求)。所以:计数变了才拉,稳态下额外请求数 ≈ 产物张数。
 *
 * ★`fetchedOnce` 这一条不能省:组图续出把任务打回 PENDING,而封面早已在库、计数也没变 ——
 *   只看「计数变没变」会让用户盯着一个没有封面的页面等,而续出的整个卖点就是「照着封面出」。
 */
export function shouldFetchAssets(input: {
  status?: string | null;
  /** 本次 task DTO 的 successCount + failedCount */
  settledCount: number;
  /** 上一次真拉产物时记下的同一个数 */
  lastSettledCount: number;
  /** 本轮轮询已经拉过一次没有 */
  fetchedOnce: boolean;
}): boolean {
  const inFlight = input.status === "PENDING" || input.status === "RUNNING";
  if (!inFlight) return true; // 终态一律拉(维持改前行为)
  if (input.settledCount <= 0) return false; // 一张都还没出,拉回来也是空的
  if (!input.fetchedOnce) return true; // 进页面/重启轮询时先对齐一次
  return input.settledCount !== input.lastSettledCount;
}

export function usePosterTaskPolling(taskId: number | null) {
  const [task, setTask] = useState<PosterTaskDto | null>(null);
  const [assets, setAssets] = useState<PosterAssetDto[]>([]);
  const [timedOut, setTimedOut] = useState(false);
  // D9:404/403 → 这条任务当前账号看不到(不存在 or 越权,后端故意不分),终态,别再轮询
  const [unavailable, setUnavailable] = useState(false);
  const cancelledRef = useRef(false);
  // 2026-05-30 — 任务到终态 / 超时后置位,refocus 不再无谓重拉 getTask+listAssets。
  const doneRef = useRef(false);
  // W1(2026-08-02)出图途中的产物变化信号(见 shouldFetchAssets)。
  // 用 ref 不用 state:它只参与「要不要发请求」的判断,进 state 会白白多一轮渲染。
  const lastSettledRef = useRef(-1);
  const fetchedOnceRef = useRef(false);
  // #12 (2026-07-04) — 付费改图会让已达终态的任务重新 RUNNING,但轮询早已 doneRef 停;
  // restart 通过 bump nonce 重跑轮询 effect(cleanup + 重新 tick),把新结果拉回来。
  const [nonce, setNonce] = useState(0);

  /**
   * 按 `shouldFetchAssets` 决定要不要真拉一次产物,并记账变化信号。
   * `force` 给**用户手动触发**的路径(refresh):那是一次明确的「给我看最新的」,
   * 不该被成本闸挡住。
   */
  const syncAssets = useCallback(async (t: PosterTaskDto, force = false) => {
    const settledCount = (t.successCount ?? 0) + (t.failedCount ?? 0);
    const wanted =
      force ||
      shouldFetchAssets({
        status: t.status,
        settledCount,
        lastSettledCount: lastSettledRef.current,
        fetchedOnce: fetchedOnceRef.current,
      });
    if (!wanted) return;
    const a = await posterService.listAssets(t.id);
    if (cancelledRef.current) return;
    // ★记账放在拿到结果之后:请求失败时不推进信号,下一拍还会重试,
    //   否则一次网络抖动就把这批产物永久跳过了。
    lastSettledRef.current = settledCount;
    fetchedOnceRef.current = true;
    setAssets((prev) => mergeAssetUrls(prev, a));
  }, []);

  // s15.6 (2026-05-08): 暴露 refresh — 详情页保存调整 / 重画后强制重拉 task + assets,
  // 让 PosterCanvas / AssetEditor 拿到新 object_key + overlay/filter, 同步左边编辑框。
  // 之前没这个, AssetEditor.invalidateQueries 跟本 hook 的 setState 没关联, 用户保存后
  // 看到的 props.asset 还是旧的 — canvas 不更新, 报"左边编辑框没同步".
  const refresh = useCallback(async () => {
    if (taskId == null || cancelledRef.current) return;
    try {
      const t = await posterService.getTask(taskId);
      if (cancelledRef.current) return;
      setTask(t);
      await syncAssets(t, true);
    } catch (e) {
      // D9:任务被删/无权限时,手动刷新也要如实落到终态,不是静默无事发生
      if (isTerminalFetchError(e) && !cancelledRef.current) {
        setUnavailable(true);
        doneRef.current = true;
      }
    }
  }, [taskId, syncAssets]);

  useEffect(() => {
    if (taskId == null) return;
    cancelledRef.current = false;
    doneRef.current = false;
    // 换任务(路由 /tasks/1 → /tasks/2 复用同一个组件实例)时,上一条的终态标志必须清掉,
    // 否则第二条会顶着第一条的「看不到」/「超时」横幅
    setUnavailable(false);
    setTimedOut(false);
    // 换任务 / restart(续出、重出)时信号必须清:上一条任务的计数留着会让新一轮
    // 第一拍误判成「没变化」而跳过 —— 而续出场景恰恰要求封面立刻可见。
    lastSettledRef.current = -1;
    fetchedOnceRef.current = false;
    const startedAt = Date.now();
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function tick() {
      if (cancelledRef.current) return;
      try {
        const t = await posterService.getTask(taskId!);
        if (cancelledRef.current) return;
        setTask(t);
        if (t.status === "SUCCESS" || t.status === "PARTIAL") {
          await syncAssets(t);
          doneRef.current = true;
          return;
        }
        if (t.status === "FAILED" || t.status === "CANCELLED") {
          // ★ 终态也要把产物拉回来:失败 ≠ 什么都没出。组图分步真跑抓到 ——
          //   封面出好并已扣费,续出那批失败被 reaper 判 FAILED,这里直接 return
          //   → 页面一张图都不显示,续出条按「0 个产物」判成「封面还没出好」,
          //   用户只剩「整体重试」(把已经付过钱的封面再出一遍)。
          //   PARTIAL 那条早就是拉了再停,这条只是漏了。
          await syncAssets(t);
          doneRef.current = true;
          return;
        }
        // ★W1:出图途中也要把已交付的产物拿回来 —— 这一句是「边出边看」的全部。
        //   成本由 shouldFetchAssets 的变化信号闸控住(计数没变就不发请求)。
        await syncAssets(t);
      } catch (e) {
        // D9:404/403 是终态,不是抖动 —— 再轮 5 分钟结果一样,只会把「没权限」显示成「加载中」
        if (isTerminalFetchError(e)) {
          if (!cancelledRef.current) setUnavailable(true);
          doneRef.current = true;
          return;
        }
        // 其余(网络抖动 / 5xx)忽略, 继续轮询
      }
      // V28.2 — 拉到 5s 一档减后端压力(用户实测早期 1-2s 太密集,backend 撑不住)。
      // 90s+ 渐进退到 10s,5min+ 超时结束。
      const elapsed = Date.now() - startedAt;
      const delay = elapsed < 90_000 ? 5000 : elapsed < 300_000 ? 10000 : 0;
      if (delay === 0) {
        setTimedOut(true);
        doneRef.current = true;
        return;
      }
      timer = setTimeout(tick, delay);
    }

    void tick();

    function onVisibility() {
      // 终态 / 超时后 refocus 不再重拉(doneRef),省一次 getTask+listAssets 往返。
      if (document.visibilityState === "visible" && !cancelledRef.current && !doneRef.current) {
        void tick();
      }
    }
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelledRef.current = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [taskId, nonce, syncAssets]);

  // #12 — 重启轮询:重置终态/超时标志并 bump nonce,让上面的 effect 重跑一轮完整轮询
  // 直到新的 SUCCESS/终态。付费改图(regenMut)成功后调用,避免"改图丢图"(结果拉不回)。
  const restart = useCallback(() => {
    doneRef.current = false;
    setTimedOut(false);
    setUnavailable(false);
    setNonce((n) => n + 1);
  }, []);

  const isPolling = task !== null && (task.status === "PENDING" || task.status === "RUNNING");
  return { task, assets, isPolling, timedOut, unavailable, refresh, restart };
}
